import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import "./registerForm.css";
import RoundedButton from "../../../../components/Buttons/RoundedButton/RoundedButton";
import {
  validatePassword,
  validateRepeatPassword,
  validateEmail,
  validateRepeatEmail,
  validateOrganizationNumber,
} from "../../../../utils/validators";
import PasswordRulesModal from "../../../../components/PasswordRulesModal/PasswordRulesModal";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { validateOptionalPhoneNumber } from "../../../../utils/validators";

function RegisterForm({ onFormSubmit, serverError }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [showPasswordRules, setShowPasswordRules] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRepeatPasswordVisibility = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (showPasswordRules) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    }

    return () => {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    };
  }, [showPasswordRules]);

  const password = watch("password", "");
  const email = watch("email", "");
  const orgNumber = watch("organization_number", "");

  const onSubmit = (formData) => {
    onFormSubmit(formData);
  };

  const orgNumberMasking = (e) => {
    const value = e.target.value;
    const cleanValue = value.replace(/[^0-9.]+/g, "");

    if (cleanValue.length >= 7) {
      const val2 = cleanValue.slice(0, 6);
      const val3 = cleanValue.slice(6, 10);
      const formattedValue = val2 + "-" + val3;
      setValue("organization_number", formattedValue);
      return;
    }

    setValue("organization_number", cleanValue);
  };

  const togglePasswordModal = (e) => {
    setShowPasswordRules(!showPasswordRules);
  };

  const tooltipText = {
    password: "Klicka för att läsa mer om reglerna kring lösenord",
  };

  const renderTooltipIcon = (tooltipHtml) => (
    <svg
      data-tooltip-id="my-tooltip"
      data-tooltip-html={tooltipHtml}
      viewBox="0 0 512 512"
      fill="currentColor"
      height="28px"
      width="28px"
      aria-hidden="true"
    >
      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0 0 114.6 0 256s114.6 256 256 256zm-86.2-346.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1 0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6-13.3 0-24-10.7-24-24v-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1 0-8.4-6.8-15.1-15.1-15.1h-58.3c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM288 352c0 17.7-14.3 32-32 32s-32-14.3-32-32 14.3-32 32-32 32 14.3 32 32z" />
    </svg>
  );

  return (
    <div className={"register-form"}>
      <div className={`register-form-container`}>
        <h1>Registrera ett konto för att skapa en annons</h1>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="register-input-fields">
            <label htmlFor="register-firstname">
              Förnamn*
              <input
                className={`input-field`}
                data-testid="register-input-first-name"
                {...register("first_name", { required: true })}
                type="text"
                id="register-firstname"
                aria-invalid={errors.first_name ? "true" : "false"}
                aria-describedby={
                  errors.first_name ? "firstname-error" : undefined
                }
              />
              {errors.first_name && (
                <span
                  id="firstname-error"
                  className="register-form-error-msg"
                  role="alert"
                >
                  Vänligen ange ett förnamn
                </span>
              )}
            </label>
            <label htmlFor="register-lastname">
              Efternamn
              <input
                className={`input-field`}
                {...register("last_name", { required: false })}
                type="text"
                id="register-lastname"
              />
            </label>
            <label htmlFor="register-email">
              E-postadress*
              <input
                className={`input-field`}
                {...register("email", {
                  validate: validateEmail,
                })}
                type="text"
                data-testid="register-input-email"
                id="register-email"
                placeholder="E-postadress"
                aria-invalid={errors.email ? "true" : "false"}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <span
                  className="register-form-error-msg"
                  id="email-error"
                  role="alert"
                >
                  {errors.email.message}
                </span>
              )}
            </label>
            <div className="register-email-div">
              <label htmlFor="register-email">Upprepa E-postadress*</label>
              <input
                className={`input-field`}
                {...register("repeat_email", {
                  validate: (value) => validateRepeatEmail(value, email),
                })}
                type="text"
                id="register-repeat-password"
                placeholder="Upprepa e-postadress"
                aria-invalid={errors.repeat_email ? "true" : "false"}
                aria-describedby={
                  errors.repeat_email ? "repeat-email-error" : undefined
                }
              />
              {errors.repeat_email && (
                <span
                  className="register-form-error-msg"
                  id="repeat-email-error"
                  role="alert"
                >
                  {errors.repeat_email.message}
                </span>
              )}
            </div>
            <label htmlFor="register-phone">
              Telefonnummer
              <input
                className={`input-field`}
                {...register("mobile", {
                  validate: validateOptionalPhoneNumber,
                })}
                type="text"
                id="register-phone"
                aria-invalid={errors.mobile ? "true" : "false"}
                aria-describedby={errors.mobile ? "phone-error" : undefined}
              />
              {errors.mobile && (
                <span
                  className="register-form-error-msg"
                  id="phone-error"
                  role="alert"
                >
                  {errors.mobile.message}
                </span>
              )}
            </label>
            <label htmlFor="register-organisation">
              Organisationsnamn
              <input
                className={`input-field`}
                {...register("organization", { required: false })}
                type="text"
                id="register-organisation"
              />
            </label>
            <label htmlFor="register-organisation-number">
              Organisationsnummer*
              <input
                className={`input-field`}
                {...register("organization_number", {
                  required: true,
                  validate: validateOrganizationNumber,
                })}
                onChange={orgNumberMasking}
                value={orgNumber}
                type="text"
                id="register-organisation-number"
                placeholder="000000-0000"
                aria-invalid={errors.organization_number ? "true" : "false"}
                aria-describedby={
                  errors.organization_number ? "org-nr-error" : undefined
                }
              />
              {errors.organization_number && (
                <span
                  className="register-form-error-msg"
                  id="org-nr-error"
                  role="alert"
                >
                  Org.nr måste vara i formatet XXXXXX-XXXX
                </span>
              )}
            </label>
            <div>
              <div className="register-password-label-container">
                <label htmlFor="register-password">Lösenord*</label>
                <button
                  onClick={togglePasswordModal}
                  aria-label="Klicka för att läsa mer om reglerna kring lösenord"
                  id="register-password-btn"
                >
                  {renderTooltipIcon(tooltipText.password)}
                </button>
              </div>

              <br></br>

              <div className="register-password-div">
                <input
                  data-testid="register-input-password"
                  className={`input-field`}
                  {...register("password", { validate: validatePassword })}
                  type={showPassword ? "text" : "password"}
                  id="register-password"
                  placeholder="Ex: Vakansa!"
                  aria-invalid={errors.password ? "true" : "false"}
                  aria-describedby={
                    errors.password ? "password-error" : undefined
                  }
                />
                <button
                  className="show-password"
                  onClick={togglePasswordVisibility}
                  type="button"
                >
                  <img
                    src={`/images/icons/eye_${
                      showPassword ? "visible" : "hidden"
                    }.svg`}
                    alt={showPassword ? "Dölj lösenord" : "Visa lösenord"}
                  />
                </button>
              </div>
              {errors.password && (
                <span
                  className="register-form-error-msg"
                  id="password-error"
                  role="alert"
                >
                  {errors.password.message}
                </span>
              )}
            </div>
            <div>
              <label htmlFor="register-repeat-password">
                Upprepa lösenord*
              </label>

              <div className="register-password-div">
                <input
                  className={`input-field`}
                  {...register("repeat_password", {
                    validate: (value) =>
                      validateRepeatPassword(value, password),
                  })}
                  data-testid="register-input-repeat-password"
                  type={showRepeatPassword ? "text" : "password"}
                  id="register-repeat-password"
                  aria-invalid={errors.repeat_password ? "true" : "false"}
                  aria-describedby={
                    errors.repeat_password ? "repeat-password-error" : undefined
                  }
                />
                <button
                  className="show-password"
                  onClick={toggleRepeatPasswordVisibility}
                  type="button"
                >
                  <img
                    src={`/images/icons/eye_${
                      showRepeatPassword ? "visible" : "hidden"
                    }.svg`}
                    alt={showRepeatPassword ? "Dölj lösenord" : "Visa lösenord"}
                  />
                </button>
              </div>
              {errors.repeat_password && (
                <span
                  className="register-form-error-msg"
                  id="repeat-password-error"
                  role="alert"
                >
                  {errors.repeat_password.message}
                </span>
              )}
            </div>
          </div>
          <p>Fält markerade med * är obligatoriska</p>

          <div className="register-form-checkbox">
            <input
              data-testid="register-checkbox"
              type="checkbox"
              id="terms"
              {...register("terms", { required: true })}
              aria-invalid={errors.terms ? "true" : "false"}
              aria-describedby={errors.terms ? "terms-error" : undefined}
            />
            <div>
              <label htmlFor="terms" className="register-form-terms-text">
                Jag bekräftar att jag har läst och godkänner{" "}
                <a
                  href="/anvandarvillkor"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  användarvillkoren
                </a>
                , inklusive att avtalsvite kan utgå vid bristande återkoppling
                eller kringgående av plattformen.*
              </label>
            </div>
          </div>
          {errors.terms && (
            <span
              className="register-form-error-msg"
              id="terms-error"
              role="alert"
            >
              Du måste acceptera användarvillkoren
            </span>
          )}
          <div className="register-form-checkbox">
            <input type="checkbox" id="news" {...register("news")} />
            <div>
              <label htmlFor="news" className="register-form-terms-text">
                Jag vill ta emot nyheter och erbjudanden från Vakansa.
              </label>
            </div>
          </div>
          <span
            data-testid="register-error-msg"
            className="register-form-error-msg-server"
          >
            {serverError}
          </span>
          <br />
          <br />
          <div className="register-form-button">
            <RoundedButton label={"Skapa konto"} color="green" size="md" />
          </div>
        </form>
      </div>
      {showPasswordRules && (
        <div className="modal-overlay">
          <PasswordRulesModal toggleModal={togglePasswordModal} />
        </div>
      )}
      <ReactTooltip id="my-tooltip" className="register-tooltip" />
    </div>
  );
}

export default RegisterForm;
